import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { 
  SupportTicketService, 
  SupportTicket, 
  TicketFilters, 
  TicketStats, 
  TicketCategory, 
  TicketPriority, 
  TicketStatus 
} from '../../../shared/services/support-ticket.service';

@Component({
  selector: 'app-support-ticket-list',
  templateUrl: './support-ticket-list.component.html',
  styleUrls: ['./support-ticket-list.component.css']
})
export class SupportTicketListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data
  tickets: SupportTicket[] = [];
  stats: TicketStats | null = null;
  categories: TicketCategory[] = [];
  priorities: TicketPriority[] = [];
  statuses: TicketStatus[] = [];

  // Loading states
  loading = false;
  loadingStats = false;
  loadingFilters = false;

  // Pagination
  currentPage = 1;
  totalPages = 1;
  totalRecords = 0;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];

  // Filters
  filterForm: FormGroup;
  searchTerm = '';

  // UI state
  selectedTicket: SupportTicket | null = null;
  showFilters = false;
  showStats = true;
  displayedColumns = ['ticket_number', 'name', 'category', 'status', 'priority', 'assigned_to', 'created_at', 'actions'];

  constructor(
    private supportTicketService: SupportTicketService,
    private fb: FormBuilder,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) {
    this.filterForm = this.fb.group({
      status: [''],
      priority: [''],
      category: [''],
      start_date: [''],
      end_date: [''],
      sort_by: ['created_at'],
      sort_order: ['DESC']
    });
  }

  ngOnInit(): void {
    this.loadInitialData();
    this.setupSearchListener();
    this.setupFilterListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load initial data
   */
  loadInitialData(): void {
    this.loadingFilters = true;
    this.loadingStats = true;

    // Load filters data
    Promise.all([
      this.supportTicketService.getCategories().toPromise(),
      this.supportTicketService.getPriorities().toPromise(),
      this.supportTicketService.getStatuses().toPromise()
    ]).then(([categoriesRes, prioritiesRes, statusesRes]) => {
      if (categoriesRes?.data) this.categories = categoriesRes.data;
      if (prioritiesRes?.data) this.priorities = prioritiesRes.data;
      if (statusesRes?.data) this.statuses = statusesRes.data;
      this.loadingFilters = false;
    }).catch(error => {
      console.error('Error loading filters:', error);
      this.loadingFilters = false;
    });

    // Load stats
    this.loadStats();

    // Load tickets
    this.loadTickets();
  }

  /**
   * Setup search listener with debounce
   */
  setupSearchListener(): void {
    // This will be called from template with search input
  }

  /**
   * Setup filter form listener
   */
  setupFilterListener(): void {
    this.filterForm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.currentPage = 1;
        this.loadTickets();
      });
  }

  /**
   * Load tickets with current filters
   */
  loadTickets(): void {
    this.loading = true;

    const filters: TicketFilters = {
      page: this.currentPage,
      limit: this.pageSize,
      ...this.filterForm.value
    };

    if (this.searchTerm.trim()) {
      filters.search = this.searchTerm.trim();
    }

    this.supportTicketService.getTickets(filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.tickets = response.data.tickets;
          this.currentPage = response.data.pagination.current_page;
          this.totalPages = response.data.pagination.total_pages;
          this.totalRecords = response.data.pagination.total_records;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading tickets:', error);
          this.loading = false;
          this.showError('Failed to load support tickets');
        }
      });
  }

  /**
   * Load statistics
   */
  loadStats(): void {
    this.loadingStats = true;

    const { start_date, end_date } = this.filterForm.value;
    
    this.supportTicketService.getTicketStats(start_date, end_date)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.stats = response.data;
          this.loadingStats = false;
        },
        error: (error) => {
          console.error('Error loading stats:', error);
          this.loadingStats = false;
        }
      });
  }

  /**
   * Handle search input
   */
  onSearchChange(event: any): void {
    this.searchTerm = event.target.value;
    this.currentPage = 1;
    this.loadTickets();
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadTickets();
  }

  /**
   * Clear all filters
   */
  clearFilters(): void {
    this.filterForm.reset({
      status: '',
      priority: '',
      category: '',
      start_date: '',
      end_date: '',
      sort_by: 'created_at',
      sort_order: 'DESC'
    });
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadTickets();
  }

  /**
   * Handle page change
   */
  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadTickets();
  }

  /**
   * Handle page size change
   */
  onPageSizeChange(event: any): void {
    this.pageSize = event.pageSize;
    this.currentPage = 1;
    this.loadTickets();
  }

  /**
   * Navigate to create new ticket
   */
  createNewTicket(): void {
    this.router.navigate(['/support-ticket/add']);
  }

  /**
   * View ticket details
   */
  viewTicket(ticket: SupportTicket): void {
    this.router.navigate(['/support-ticket/view', ticket.id]);
  }

  /**
   * Edit ticket
   */
  editTicket(ticket: SupportTicket): void {
    this.router.navigate(['/support-ticket/edit', ticket.id]);
  }

  /**
   * Delete ticket
   */
  deleteTicket(ticket: SupportTicket): void {
    if (confirm(`Are you sure you want to delete ticket ${ticket.ticket_number}?`)) {
      this.supportTicketService.deleteTicket(ticket.id!)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.showSuccess('Ticket deleted successfully');
            this.loadTickets();
            this.loadStats();
          },
          error: (error) => {
            console.error('Error deleting ticket:', error);
            this.showError('Failed to delete ticket');
          }
        });
    }
  }

  /**
   * Export tickets
   */
  exportTickets(): void {
    // TODO: Implement export functionality
    this.showInfo('Export functionality coming soon');
  }

  /**
   * Refresh data
   */
  refreshData(): void {
    this.loadTickets();
    this.loadStats();
  }

  /**
   * Toggle filters panel
   */
  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  /**
   * Toggle stats panel
   */
  toggleStats(): void {
    this.showStats = !this.showStats;
  }

  /**
   * Get status color
   */
  getStatusColor(status: string): string {
    return this.supportTicketService.getStatusColor(status);
  }

  /**
   * Get priority color
   */
  getPriorityColor(priority: string): string {
    return this.supportTicketService.getPriorityColor(priority);
  }

  /**
   * Get status text
   */
  getStatusText(status: string): string {
    return this.supportTicketService.getStatusText(status);
  }

  /**
   * Get priority text
   */
  getPriorityText(priority: string): string {
    return this.supportTicketService.getPriorityText(priority);
  }

  /**
   * Format date
   */
  formatDate(dateString: string): string {
    return this.supportTicketService.formatDate(dateString);
  }

  /**
   * Get time ago
   */
  getTimeAgo(dateString: string): string {
    return this.supportTicketService.getTimeAgo(dateString);
  }

  /**
   * Show success message
   */
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }

  /**
   * Show info message
   */
  private showInfo(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: ['info-snackbar']
    });
  }
}
